import { Input } from './input.mjs';
import { create, registerCustomElement } from '../utils.mjs';

import classes from './mcp-list.module.scss';
import { getPackageIconSource } from '../utils.mjs';
import common from '../common.module.scss';
import { I18n } from '../helpers/i18n.mjs';

type MCPItem = {
	name: string;
	type: string;
	toolIds: string[];
	tools: { id: string; isDeprecated: boolean }[];
};

type MCPListInputInstructions = {
	description?: string;
	items: MCPItem[];
};

const createMCPRow = ({ name, tools }: MCPItem) => {
	const item = create(`li.${classes.item}`);

	// A11y attributes
	item.role = 'button';
	item.tabIndex = 0;
	item.ariaLabel = name;

	const wrapper = create(`div.${classes.headerWrapper}`);

	const header = create(`div.${classes.header}`);
	wrapper.textContent = name;

	const icon = create(`img.${classes.mcpIcon}`) as HTMLImageElement;
	icon.src = getPackageIconSource('mcp-client');

	const activePill = create(`span.${common.pill}.${common.pillSuccess}`);
	const activeToolCount = tools?.filter((tool) => !tool.isDeprecated)?.length || 0;
	activePill.textContent = I18n.l('aiAgent.mcpToolCountLabel', { count: activeToolCount });

	const invalidToolCount = tools?.filter((tool) => tool.isDeprecated)?.length || 0;
	const invalidPill = create(`span.${common.pill}.${common.pillWarning}`);
	invalidPill.textContent = I18n.l('aiAgent.mcpInvalidToolCountLabel', { count: invalidToolCount });

	const expander = create(`button.${classes.expander}.${common.button}.${common.buttonSmall}.${common.buttonGhost}`);
	expander.tabIndex = -1;

	const expanderIcon = create(`i.far.fa-chevron-down.${classes.expanderIcon}`);

	expander.append(expanderIcon);
	header.prepend(icon);
	header.appendChild(wrapper);

	if (activeToolCount) {
		header.append(activePill);
	}

	if (invalidToolCount) {
		header.append(invalidPill);
	}

	header.append(expander);
	item.append(header);

	// Tool list body handles the height transition of the list
	const toolListBody = create(`div.${classes.toolListBody}`);
	const toolList = create(`ul.${classes.toolList}.${classes.hidden}`);

	toolList.append(
		...tools.map((tool) => {
			const toolItem = create(`li.${classes.toolItem}`);
			toolItem.style.color = tool.isDeprecated ? '#b4b4b4' : 'inherit';
			toolItem.textContent = tool.id;
			return toolItem;
		}),
	);

	toolListBody.append(toolList);
	item.append(toolListBody);

	const toggleToolList = () => {
		toolList.classList.toggle(classes.hidden);
		expanderIcon.classList.toggle(classes.rotate180);
		toolListBody.style.height = `${toolList.offsetHeight}px`;
	};

	item.addEventListener('click', () => toggleToolList());
	item.addEventListener('keypress', (event) => {
		if (event.key === 'Enter') {
			toggleToolList();
		}
	});

	return item;
};

export class MCPListInput extends Input {
	async _build(instructions: MCPListInputInstructions) {
		// .wrapper is used here so that collapsing correctly hides this component
		const wrapper = create('div.wrapper');

		if (instructions.description) {
			const description = create(`div.${classes.description}`);
			description.textContent = instructions.description;
			wrapper.appendChild(description);
		}

		const list = create(`ul.${classes.list}`);

		wrapper.appendChild(list);

		instructions.items.forEach((mcpServer) => {
			const row = createMCPRow(mcpServer);

			list.appendChild(row);
		});
		this.appendChild(wrapper);

		this.append(wrapper);
	}
}

registerCustomElement('imt-input-mcplist', MCPListInput);
